import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Alert,
} from '@mui/material';
import { Progress } from '@backstage/core-components';
import { Campaign } from '@internal/backstage-plugin-bidone-backend';
import { useCopyFromRubric, useRubricChecks } from '../../hooks/useCampaigns';

type CopyFromRubricDialogProps = {
  open: boolean;
  campaign: Campaign;
  onClose: () => void;
};

// Copy rubric checks into the campaign, taking ownership of them — a check
// belongs to exactly one of the rubric or a single campaign at a time, so
// checks already held by an active campaign are disabled here.
export const CopyFromRubricDialog = ({
  open,
  campaign,
  onClose,
}: CopyFromRubricDialogProps) => {
  const { data: rubricChecks, isFetching, error } = useRubricChecks();
  const copyFromRubric = useCopyFromRubric();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setSelected(new Set());
      copyFromRubric.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (id: string) =>
    setSelected(existing => {
      const next = new Set(existing);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const copyLabel = `Copy ${selected.size || ''} check${
    selected.size === 1 ? '' : 's'
  }`;

  const handleCopy = async () => {
    await copyFromRubric.mutateAsync({
      campaignId: campaign.id,
      rubricCheckIds: Array.from(selected),
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copy checks from rubric</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Copied checks move from the rubric to this campaign. They return to
          the rubric when the campaign ends with migration.
        </Typography>
        {error ? (
          <Alert severity="error">{(error as Error).message}</Alert>
        ) : null}
        {copyFromRubric.error ? (
          <Alert severity="error">
            {(copyFromRubric.error as Error).message}
          </Alert>
        ) : null}
        {isFetching && !rubricChecks && <Progress />}
        <List dense>
          {(rubricChecks ?? []).map(check => {
            const owned = !!check.ownedByCampaignId;
            return (
              <ListItemButton
                key={check.id}
                disabled={owned}
                onClick={() => toggle(check.id)}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    size="small"
                    checked={selected.has(check.id)}
                    disabled={owned}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      {check.name}
                      {check.scorecard ? (
                        <Chip
                          sx={{ ml: 1 }}
                          size="small"
                          variant="outlined"
                          label={check.scorecard}
                        />
                      ) : null}
                      {owned ? (
                        <Chip
                          sx={{ ml: 1 }}
                          size="small"
                          color="secondary"
                          label={`Owned by ${check.ownedByCampaignName}`}
                        />
                      ) : null}
                    </>
                  }
                  secondary={check.description}
                />
              </ListItemButton>
            );
          })}
        </List>
        {rubricChecks && rubricChecks.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            No rubric checks are configured.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={selected.size === 0 || copyFromRubric.isPending}
          onClick={() => {
            handleCopy().catch(() => undefined);
          }}
        >
          {copyFromRubric.isPending ? 'Copying…' : copyLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
